/**
 * MShorts Public Web Client Application Entry
 */
import './styles/theme.css';
import './styles/styles.css';

import { getAppConfig } from './config.js';
import { createAds } from './lib/ads.js';
import { createApi } from './lib/api.js';
import { createConsent } from './lib/consent.js';
import { analytics, announcement, footer, header, notice, offerLinks, sidebar, sidebarMode } from './lib/chrome.js';
import { h, spinner } from './lib/dom.js';
import { createRouter } from './lib/router.js';
import { createText } from './lib/text.js';
import { access } from './lib/store.js';
import { installLinks, onInstallChange, registerWorker } from './lib/pwa.js';
import { message } from './lib/ui.js';
import { stopActivePlayer } from './lib/player.js';

const config = getAppConfig();
const { mount, design, site, pages } = config;

const root = config.root ?? mount;
const languages = config.languages ?? [];
const lang = config.lang;
const first = languages[0]?.code ?? lang;

// Token-protected access
const api = createApi(mount, () => {
  const token = access.token();
  return token ? { 'X-Access-Token': token } : {};
});

const text = createText(design);
const consent = createConsent(design);
const ads = createAds({ api, design, text, consent, mount });

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const routes = [
  [/^\/$/, 'home'],
  [/^\/browse$/, 'browse'],
  [/^\/search$/, 'search'],
  [/^\/rankings$/, 'rankings'],
  [/^\/library$/, 'library'],
  [new RegExp(`^/drama/(?<id>${UUID})$`, 'i'), 'drama'],
  [new RegExp(`^/watch/(?<id>${UUID})/(?<episode>\\d+)$`, 'i'), 'watch'],
  [/^\/page\/(?<slug>[a-z0-9-]+)$/, 'page'],
  [/^\/subscribe$/, 'subscribe'],
  [/^\/subscribe\/done$/, 'subscribe-done'],
];

const views = {
  home: () => import('./views/home.js'),
  browse: () => import('./views/browse.js'),
  search: () => import('./views/search.js'),
  rankings: () => import('./views/rankings.js'),
  library: () => import('./views/library.js'),
  drama: () => import('./views/drama.js'),
  watch: () => import('./views/watch.js'),
  page: () => import('./views/page.js'),
  subscribe: () => import('./views/subscribe.js'),
  'subscribe-done': () => import('./views/subscribe-done.js'),
  notfound: () => import('./views/notfound.js'),
};

const main = h('main', { id: 'main' });
const ctx = {
  api,
  ads,
  consent,
  design,
  site,
  pages,
  mount,
  root,
  lang,
  first,
  languages,
  preview: window.top !== window,
  text: { ...text, t: text.t },
  templateBase: '/',
  href: (path) => root.replace(/\/$/, '') + path,
  router: null,
  searchInput: null,
  refreshOffer() {
    ads.refresh().then(() => offerLinks(ctx, ads.offer()));
  },
  setTitle(page) {
    const name = ctx.site.name || site.name;
    const tagline = ctx.design['brand.tagline'];
    document.title = page ? String(ctx.design['seo.title'] || '{page} | {name}').replace('{page}', page).replace('{name}', name) : tagline ? `${name} | ${tagline}` : name;
  },
};

let current = 0;
let shown = [];
async function render(match, url) {
  const mine = ++current;
  stopActivePlayer();
  for (const node of shown) node.dispatchEvent(new Event('vs:leave'));
  for (const media of document.querySelectorAll('video, audio')) {
    try {
      media.pause();
      media.removeAttribute('src');
      media.load();
    } catch {}
  }
  main.replaceChildren(spinner(text.t('loading')));
  top?.setActive?.(match.path);
  side?.setActive?.(match.path, url.search);
  try {
    const view = (await views[match.name]()).default;
    const result = await view(ctx, match, url);
    if (mine !== current) {
      for (const node of [result].flat().filter((n) => n instanceof Node)) {
        node.dispatchEvent(new Event('vs:leave'));
      }
      stopActivePlayer();
      return;
    }
    shown = [result].flat().filter((n) => n instanceof Node);
    main.replaceChildren(...shown);
    if (typeof window.gtag === 'function') {
      window.gtag('set', {
        page_title: document.title,
        page_location: location.href,
        page_path: match.path,
      });
    }
  } catch (err) {
    if (mine !== current) return;
    stopActivePlayer();
    if (err && (err.status === 404 || err.status === 400) && match.name !== 'notfound') {
      try {
        const result = (await views.notfound()).default(ctx, match, url);
        if (mine !== current) return;
        shown = [result].flat().filter((n) => n instanceof Node);
        main.replaceChildren(...shown);
        return;
      } catch (inner) {
        console.error(inner);
      }
    }
    console.error(err);
    shown = [];
    main.replaceChildren(h('div', { class: 'wrap' }, message(ctx, 'error', () => render(match, url))));
  }
}

ctx.router = createRouter({ mount: root, routes, render });

let top;
let side = null;
const backdrop = h('div', { class: 'side-backdrop', on: { click: () => ctx.openSidebar(false, true) } });

ctx.openSidebar = (open, back = false) => {
  if (!document.body.classList.contains('has-sidebar') || document.body.classList.contains('side-open') === open) return;
  document.body.classList.toggle('side-open', open);
  document.querySelector('.menu-toggle')?.setAttribute('aria-expanded', String(open));
  if (open) side?.querySelector('a, button')?.focus();
  else if (back) document.querySelector('.menu-toggle')?.focus();
};

function applyThemeVariables(d) {
  const r = document.documentElement;
  if (d['look.accent']) {
    r.style.setProperty('--accent', d['look.accent']);
    r.style.setProperty('--link', d['look.accent']);
  }
  if (d['look.accent_ink']) {
    r.style.setProperty('--accent-ink', d['look.accent_ink']);
  }
  if (d['look.dark_bg']) {
    r.style.setProperty('--bg', d['look.dark_bg']);
    r.style.setProperty('--header-bg', d['look.dark_bg'] + 'e6');
  }
  if (d['look.dark_surface']) {
    r.style.setProperty('--surface', d['look.dark_surface']);
    r.style.setProperty('--surface-hover', `color-mix(in srgb, ${d['look.dark_surface']} 80%, white)`);
  }
  if (d['look.dark_text']) {
    r.style.setProperty('--text', d['look.dark_text']);
  }
  if (d['look.radius']) {
    const rad = d['look.radius'] === 'none' ? '0px' : d['look.radius'] === 'round' ? '24px' : '12px';
    r.style.setProperty('--radius', rad);
    r.style.setProperty('--btn-radius', rad);
  }
  document.body.className = `dim-${d['cinema.hero_dim'] ?? 'medium'} hover-${d['cinema.hover'] ?? 'lift'}`;
}

function buildChrome() {
  const withSide = sidebarMode(ctx.design);
  document.body.classList.toggle('has-sidebar', withSide);
  document.body.classList.remove('side-open');
  top = header(ctx);
  side = withSide ? sidebar(ctx) : null;
  const appEl = document.getElementById('app');
  if (appEl) {
    appEl.replaceChildren(...[side, side ? backdrop : null, announcement(ctx), top, main, footer(ctx)].filter(Boolean));
  }
  offerLinks(ctx, ads.offer());
  installLinks(ctx);
}

// Initial theme & chrome setup
applyThemeVariables(ctx.design);
buildChrome();
ctx.setTitle();

const bar = notice(ctx);
if (bar) document.body.append(bar);
onInstallChange(() => installLinks(ctx));
registerWorker({ mount, design: ctx.design });
ads.ready.then(() => {
  const sticky = ads.sticky();
  if (sticky) document.body.append(sticky);
  offerLinks(ctx, ads.offer());
});

// Live preview postMessage listener for dashboard Design tab
if (window.parent && window.parent !== window) {
  const style = document.createElement('style');
  style.id = 'vs-draft';
  document.head.append(style);

  window.addEventListener('message', (e) => {
    const msg = e.data;
    if (!msg || msg.type !== 'vs-preview') return;
    if (msg.design && typeof msg.design === 'object') {
      Object.assign(ctx.design, msg.design);
    }
    if (typeof msg.siteName === 'string' && msg.siteName) {
      ctx.site.name = msg.siteName;
    }
    if (typeof msg.css === 'string') {
      style.textContent = msg.css;
    }
    applyThemeVariables(ctx.design);
    buildChrome();
    ctx.setTitle();
    document.querySelector(".notice")?.remove();
    const previewNotice = notice(ctx, true);
    if (previewNotice) document.body.append(previewNotice);
  });

  window.parent.postMessage({ type: 'vs-preview-ready' }, '*');
}

// Immediate router startup with background configuration synchronization
async function startApp() {
  // Start routing immediately - zero blocking latency
  ctx.router.start();

  consent.onAccept(() => {
    analytics(ctx.design);
    const sticky = ads.sticky();
    if (sticky) document.body.append(sticky);
  });

  // Sync latest configuration in background without delaying initial render
  try {
    const cfg = await api.get('v1/config');
    if (cfg) {
      let changed = false;
      if (cfg.design) {
        Object.assign(ctx.design, cfg.design);
        changed = true;
      }
      const siteName = cfg.design?.['brand.name'] || cfg.name;
      if (siteName && siteName !== ctx.site.name) {
        ctx.site.name = siteName;
        changed = true;
      }
      if (changed) {
        applyThemeVariables(ctx.design);
        buildChrome();
        ctx.setTitle();
      }
      if (consent.ok()) analytics(ctx.design);
      if (!document.querySelector(".notice")) {
        const bar = notice(ctx);
        if (bar) document.body.append(bar);
      }
    }
  } catch (e) {
    console.warn('Background v1/config sync:', e);
  }
}

startApp();
